import { BRIDGE_SUITS, type BridgeCard, type BridgeSuit, cardId, fullDeck, sameCard } from './cards.js';
import type { BridgeTrump } from './contract.js';
import { type BridgeTrickPlay, trickWinner } from './play.js';
import { type BridgeSeat, partnerOf } from './seats.js';
import type { BridgeView } from './view.js';

/** `easy` plays any legal card. `clever` plays by simple, sound rules of thumb. */
export type BridgeRobotLevel = 'easy' | 'clever';

/**
 * A robot sees exactly what a human in its seat would: a `BridgeView`, never the game. It cannot
 * know a hidden hand, so it cannot cheat; the only extra it uses is counting cards already played
 * (which any player at the table can do).
 */

/** The declarer's trump choice: the engine's suggestion, which is sound for a robot. */
export function robotChooseTrump(view: BridgeView): BridgeTrump {
  return view.contract.suggestedTrump ?? 'NT';
}

/** The cards the viewer cannot see and that have not been played: held by the opponents. */
export function unseenCards(view: BridgeView): BridgeCard[] {
  const known = new Set<string>();
  for (const c of view.hand) known.add(cardId(c));
  if (view.dummy) for (const c of view.dummy.hand) known.add(cardId(c));
  for (const t of view.tricks) for (const p of t.plays) known.add(cardId(p.card));
  for (const p of view.trick) known.add(cardId(p.card));
  return fullDeck().filter((c) => !known.has(cardId(c)));
}

const lowest = (cards: readonly BridgeCard[]): BridgeCard =>
  cards.reduce((a, b) => (b.rank < a.rank ? b : a));
const highest = (cards: readonly BridgeCard[]): BridgeCard =>
  cards.reduce((a, b) => (b.rank > a.rank ? b : a));

/** Lead a card. */
function chooseLead(view: BridgeView, legal: readonly BridgeCard[], trump: BridgeTrump): BridgeCard {
  const unseen = unseenCards(view);
  const seat = view.turn;
  const ours = new Set<BridgeSeat>([seat, partnerOf(seat)]);
  const declaring = ours.has(view.contract.declarer);

  // The declaring side draws trumps while the opponents still hold some.
  if (declaring && trump !== 'NT') {
    const myTrumps = legal.filter((c) => c.suit === trump);
    const theirTrumps = unseen.filter((c) => c.suit === trump);
    if (myTrumps.length > 0 && theirTrumps.length > 0) return highest(myTrumps);
  }

  // Cash a master card: the highest card of its suit still out, in the longest such suit.
  const masters = legal.filter((c) => !unseen.some((u) => u.suit === c.suit && u.rank > c.rank));
  if (masters.length > 0) {
    const length = (suit: BridgeSuit) => legal.filter((c) => c.suit === suit).length;
    return masters.reduce((a, b) => (length(b.suit) > length(a.suit) || (length(b.suit) === length(a.suit) && b.rank > a.rank) ? b : a));
  }

  // Otherwise: from the longest non-trump suit, the top of a touching honour pair, else the lowest card.
  const nonTrump = legal.filter((c) => c.suit !== trump);
  const pool = nonTrump.length > 0 ? nonTrump : legal;
  let bestSuit: BridgeSuit = (pool[0] as BridgeCard).suit;
  let bestLen = -1;
  for (const suit of BRIDGE_SUITS) {
    const len = pool.filter((c) => c.suit === suit).length;
    if (len > bestLen) {
      bestLen = len;
      bestSuit = suit;
    }
  }
  const suited = pool.filter((c) => c.suit === bestSuit).sort((a, b) => b.rank - a.rank);
  const top = suited[0] as BridgeCard;
  const next = suited[1];
  if (next && top.rank >= 12 && top.rank - next.rank === 1) return top; // KQ, QJ, AK-type sequence
  return lowest(suited);
}

/** Follow to a trick already started. */
function chooseFollow(view: BridgeView, legal: readonly BridgeCard[], trump: BridgeTrump): BridgeCard {
  const seat = view.turn;
  const trick = view.trick;
  const led = (trick[0] as BridgeTrickPlay).card.suit;
  const winning = trickWinner(trick, trump);
  const winningCard = (trick.find((p) => p.seat === winning) as BridgeTrickPlay).card;
  const partnerWinning = winning === partnerOf(seat);
  const last = trick.length === 3;
  const following = legal.every((c) => c.suit === led) && legal.some((c) => c.suit === led);

  if (following) {
    if (partnerWinning) return lowest(legal);
    // An opponent is winning. If they trumped, following suit cannot beat them.
    if (trump !== 'NT' && winningCard.suit === trump) return lowest(legal);
    const winners = legal.filter((c) => c.rank > winningCard.rank);
    if (winners.length === 0) return lowest(legal);
    return last ? lowest(winners) : highest(winners);
  }

  // Void in the suit led: trump in if it helps, otherwise discard.
  const trumps = trump === 'NT' ? [] : legal.filter((c) => c.suit === trump);
  if (trumps.length > 0 && !partnerWinning) {
    const beating = winningCard.suit === trump ? trumps.filter((c) => c.rank > winningCard.rank) : trumps;
    if (beating.length > 0) return lowest(beating);
  }
  const discards = legal.filter((c) => c.suit !== trump);
  return lowest(discards.length > 0 ? discards : legal);
}

/**
 * The card a robot plays. `view.canAct` must be true and the phase `play`. `random` (a function
 * returning [0, 1)) is used by the easy robot only, so a clever game is fully deterministic.
 */
export function robotChooseCard(view: BridgeView, level: BridgeRobotLevel, random: () => number): BridgeCard {
  const legal = view.legalPlays;
  if (legal.length === 0) throw new Error('robotChooseCard: nothing to play (not this seat\'s turn?)');
  if (legal.length === 1) return legal[0] as BridgeCard;
  if (level === 'easy') return legal[Math.floor(random() * legal.length)] as BridgeCard;
  const trump = view.contract.trump as BridgeTrump;
  const card = view.trick.length === 0 ? chooseLead(view, legal, trump) : chooseFollow(view, legal, trump);
  // Defensive: whatever a heuristic returns must be legal.
  return legal.find((c) => sameCard(c, card)) ?? (legal[0] as BridgeCard);
}

